import React from 'react';
import { Icon, Button } from '@/components/atoms';

export interface PaginationProps {
  /** Current page number (1-based) */
  currentPage: number;
  /** Total number of items */
  totalItems: number;
  /** Number of items per page */
  itemsPerPage: number;
  /** Available items per page options */
  itemsPerPageOptions?: number[];
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when items per page changes */
  onItemsPerPageChange: (itemsPerPage: number) => void;
  /** Whether pagination is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  itemsPerPageOptions = [10, 25, 50, 100],
  onPageChange,
  onItemsPerPageChange,
  disabled = false,
  className = '',
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1 && !disabled) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages && !disabled) {
      onPageChange(currentPage + 1);
    }
  };

  const handleItemsPerPageChange = (value: string) => {
    const newItemsPerPage = parseInt(value, 10);
    if (newItemsPerPage !== itemsPerPage && !disabled) {
      // Reset to page 1 when changing items per page
      onItemsPerPageChange(newItemsPerPage);
      onPageChange(1);
    }
  };

  if (totalItems === 0) {
    return (
      <div className={`flex items-center justify-between px-6 py-4 border-t border-neutral-200 ${className}`}>
        <div className="text-sm text-neutral-600">No items to display</div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-neutral-200 ${className}`}>
      {/* Items info and rows per page selector */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="text-sm text-neutral-600">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="items-per-page" className="text-sm text-neutral-600 whitespace-nowrap">
            Rows per page:
          </label>
          <select
            id="items-per-page"
            value={itemsPerPage.toString()}
            onChange={(e) => handleItemsPerPageChange(e.target.value)}
            disabled={disabled}
            className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option.toString()}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Page navigation */}
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handlePrevious}
          disabled={disabled || currentPage === 1}
          className="flex items-center gap-1"
        >
          <Icon name="ChevronLeft" size={16} />
          Previous
        </Button>
        <div className="flex items-center gap-1">
          <span className="text-sm text-neutral-600">
            Page <span className="font-medium">{currentPage}</span> of{' '}
            <span className="font-medium">{totalPages}</span>
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleNext}
          disabled={disabled || currentPage === totalPages}
          className="flex items-center gap-1"
        >
          Next
          <Icon name="ChevronRight" size={16} />
        </Button>
      </div>
    </div>
  );
};

